<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow, parseISO } from 'date-fns';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  comments: { type: Array, default: () => [] },
  selectedId: { type: Number, default: null },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const isEmpty = computed(() => !props.isLoading && props.comments.length === 0);

const relativeCommentedAt = commentedAt => {
  if (!commentedAt) return t('COMMENTS.LIST.NO_DATE');
  return formatDistanceToNow(parseISO(commentedAt), { addSuffix: true });
};

const handleSelect = id => emit('select', id);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-y-auto">
    <template v-if="isLoading">
      <div
        v-for="skeletonRow in 5"
        :key="skeletonRow"
        data-testid="comment-row-skeleton"
        class="flex flex-col w-full gap-2 px-4 py-4 border-b border-n-weak animate-pulse"
      >
        <div class="w-1/3 h-3 rounded bg-n-slate-4" />
        <div class="w-full h-3 rounded bg-n-slate-4" />
        <div class="w-2/3 h-2.5 rounded bg-n-slate-4" />
      </div>
    </template>
    <div
      v-else-if="isEmpty"
      data-testid="comments-empty-state"
      class="flex flex-col items-center justify-center flex-1 gap-2 px-6 py-12 text-center"
    >
      <Icon icon="i-lucide-message-circle" class="text-n-slate-9 size-8" />
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('COMMENTS.LIST.EMPTY_TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11">
        {{ t('COMMENTS.LIST.EMPTY_SUBTITLE') }}
      </p>
    </div>
    <template v-else>
      <button
        v-for="comment in comments"
        :key="comment.id"
        type="button"
        data-testid="comment-row"
        class="flex flex-col items-start w-full gap-1 px-4 py-3 text-left border-b border-n-weak hover:bg-n-alpha-1"
        :class="{
          'bg-n-alpha-2': selectedId === comment.id,
          'line-through opacity-60': comment.status === 'deleted',
        }"
        @click="handleSelect(comment.id)"
      >
        <div class="flex items-center justify-between w-full gap-2">
          <div class="flex items-center min-w-0 gap-1.5">
            <span
              v-if="!comment.handledAt"
              data-testid="comment-row-unread"
              class="rounded-full bg-n-blue-9 size-2 shrink-0"
            />
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ comment.authorName }}
            </span>
            <Icon
              v-if="comment.status === 'hidden'"
              icon="i-lucide-lock-keyhole"
              class="text-n-slate-10 size-3.5 shrink-0"
            />
          </div>
          <span class="text-xs text-n-slate-10 shrink-0">
            {{ relativeCommentedAt(comment.commentedAt) }}
          </span>
        </div>
        <p class="w-full text-sm truncate text-n-slate-11">
          {{ comment.message }}
        </p>
        <p class="w-full text-xs truncate text-n-slate-9">
          {{ comment.socialPost?.message }}
        </p>
      </button>
    </template>
  </div>
</template>
